import {
    Form,
    FormLayout,
    TextField,
    Button,
    ResourceItem,
    ResourceList,
    LegacyCard,
    Layout,
    Toast,
    Text,
} from "@shopify/polaris";
import {useState, useCallback, useEffect, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import Api from '../../helpers/api';
import { normalize_phone_number } from '../../helpers/utils';
import Paginator from "../../components/Paginator";

function findMatchingLocation(query, customerData) {
    const normalizedPhoneNumber = normalize_phone_number(query);

    return normalizedPhoneNumber
        ? customerData.locations.find(item => item.normalized_phone_number === normalizedPhoneNumber)
        : null;
}

function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
}

const SearchCustomer = props => {
    const history = useHistory();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [errors, setErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState({currentPage: 1, lastPage: 1})
    const [toast, setToast] = useState({
        active: false,
        message: ''
    });

    const queryString = useQuery();

    const handleSubmit = () => {
        if (query == '') {
            setErrors('Please type something in the search field.')
            return;
        };

        history.push('/customers?' + (new URLSearchParams({q: query})));
        loadCustomers(query, true);
    }

    const loadCustomers = async (q, canRedirect, page = 1) => {
        setErrors(null);
        setLoading(true);
        const { data: result } = await Api.findCustomer(q, page);
        if (result.data.length === 1 && canRedirect) {
            const customerData = result.data[0];
            const location = findMatchingLocation(q, customerData);

            if (location) {
                history.push(`/locations/${location.id}`);
            } else {
                assignCustomer(customerData);
            }
        } else if (result.data.length === 0) {
            setToast({active: true, 'message': 'There are no results matching your search.'});
        } else {
            setResults(result.data);
            setMeta({currentPage: result.meta.current_page, lastPage: result.meta.last_page});
        }
        setLoading(false);
    };

    useEffect(() => {
        if (queryString.get('bill_to_id')) {
            const billToQuery = 'Bill To: ' + queryString.get('bill_to_id')
            setQuery(billToQuery)
            loadCustomers(billToQuery, false);
        }

        if (queryString.get('q') == '' || queryString.get('q') == null) {
            return;
        }

        setQuery(queryString.get('q'));
        loadCustomers(queryString.get('q'), false);
    }, []);

    const assignCustomer = customer => {
        props.onCustomerSelect(customer);
        setResults([]);
        setQuery('');
    }

    const handleQueryChange = useCallback(
        (value) => setQuery(value),
        [],
    );

    const toastMarkup = toast.active ? (
        <Toast content={toast.message} onDismiss={() => {setToast({message: '', active: false})}} />
      ) : null;

    return (
        <Layout>
        <Layout.Section>
            <Form onSubmit={handleSubmit}>
                <FormLayout>
                    <TextField
                        value={query}
                        onChange={handleQueryChange}
                        label="Search by account number or phone"
                        type="text"
                        error={errors}
                        helpText={
                            <span>Search by Phone or Customer Account Number</span>
                        }
                        connectedRight={<Button loading={loading} submit  variant="primary">Search</Button>}
                    />
                </FormLayout>
            </Form>
        </Layout.Section>

        {results.length > 0 && (
            <Layout.Section>
                <LegacyCard title="Search Results">
                    <ResourceList
                        resourceName={{ singular: 'customer', plural: 'customers' }}
                        items={results}
                        renderItem={(item) => {
                            return (
                                <ResourceItem
                                    id={item.id}
                                    onClick={event => { assignCustomer(item) }}
                                    accessibilityLabel={`View details for account ${item.account_number}`}
                                >
                                    <h3>
                                        <Text  variant="bodyMd" as="span" fontWeight="semibold">{`#${item.account_number} - ${item.additional_data.first_name ?? ''} ${item.additional_data.last_name ?? ''}`}</Text>
                                    </h3>
                                    <div>{`Phone: ${item.additional_data.phone} - Email: ${item.email}`}</div>
                                </ResourceItem>
                            );
                        }}
                    />
                </LegacyCard>
                <Paginator currentPage={meta.currentPage} lastPage={meta.lastPage} show={true} callback={(page) => loadCustomers(query, false, page)} />
            </Layout.Section>
        )}
        {toastMarkup}
    </Layout>
    );
}

export default SearchCustomer;
